import { useQuery } from "@tanstack/react-query";
import { fetchAllEditionBpas, researchCms } from "@/api/researchCms.service";
import { type ImportSelection, practiceAreaLabel, sourceCandidates } from "../../utils/bpaImport";
import type { ImportUrlContext } from "../../utils/bpaImportUrlState";
import { isMergeHubForImport } from "../../utils/bpaMerge";
import { isSplitHubForImport } from "../../utils/bpaSplit";

type Args = {
  editionId: string;
  bpaId: string;
  pickedEditionId: string | null;
};

/** What both the import page and the review screen load: the target, the publications and the source's practice areas. */
export function useImportData({ editionId, bpaId, pickedEditionId }: Args) {
  const targetQuery = useQuery({
    queryKey: ["bpa-import-target", editionId, bpaId],
    queryFn: async () => {
      const [editionBpas, editions] = await Promise.all([
        fetchAllEditionBpas({ editionId }),
        researchCms.getEditions({ editionId, limit: 1 }),
      ]);
      return {
        editionBpas,
        editionName: editions.editionConfigs[0]?.editionName ?? "",
        editionStatus: editions.editionConfigs[0]?.status ?? null,
      };
    },
    staleTime: 0,
    refetchOnMount: "always",
  });
  const publicationsQuery = useQuery({
    queryKey: ["research-publications-list"],
    queryFn: async () => (await researchCms.getPublications()).publications,
  });
  const publications = publicationsQuery.data ?? [];
  const editionEntries = publications.flatMap(pub =>
    pub.editions.map(edition => ({ edition, publication: pub })),
  );
  const knownPickedEdition = editionEntries.some(
    entry => entry.edition.editionId === pickedEditionId,
  );
  const sourceEditionId = knownPickedEdition && pickedEditionId ? pickedEditionId : editionId;
  const sourceInAnotherEdition = sourceEditionId !== editionId;
  const sourceEdition = editionEntries.find(entry => entry.edition.editionId === sourceEditionId);

  const sourceBpasQuery = useQuery({
    queryKey: ["bpa-import-source-bpas", sourceEditionId],
    queryFn: () => fetchAllEditionBpas({ editionId: sourceEditionId }),
    enabled: sourceInAnotherEdition,
  });
  const sourceRows = sourceInAnotherEdition ? sourceBpasQuery.data : targetQuery.data?.editionBpas;

  const target = targetQuery.data?.editionBpas.find(bpa => bpa.bpaId === bpaId) ?? null;
  const candidates =
    target && sourceRows
      ? sourceCandidates(
          sourceRows,
          { id: target.id, editionId: target.editionId },
          sourceEditionId,
        )
      : [];

  function sourceNameOf(sourceEditionBpaId: string | null): string {
    const source = candidates.find(bpa => bpa.id === sourceEditionBpaId);
    if (!source) return "";
    return practiceAreaLabel(
      {
        name: source.bpaName,
        editionId: source.editionId,
        editionName: sourceEdition?.edition.editionName ?? null,
        publicationName: sourceEdition?.publication.name ?? null,
      },
      editionId,
    );
  }

  function urlContext(canDelete: boolean): ImportUrlContext | null {
    if (!target) return null;
    return {
      targetEditionBpaId: target.id,
      targetEditionId: editionId,
      offeredSourceIds: candidates.map(candidate => candidate.id),
      sourceEditionStatus: sourceEdition?.edition.status ?? null,
      canDelete,
      isHubForImport: (hub, importing) => {
        const hubSource = candidates.find(
          candidate => candidate.id === importing.sourceEditionBpaId,
        );
        if (importing.purpose === "merge") {
          return isMergeHubForImport(hub, {
            editionId,
            targetBpaId: bpaId,
            sourceScheduledIntoTarget:
              !!hubSource && hubSource.mergeTargetEditionBpaId === target.id,
          });
        }
        return (
          importing.purpose === "split" &&
          !!hubSource &&
          isSplitHubForImport(hub, { editionId: sourceEditionId, bpaId: hubSource.bpaId })
        );
      },
    };
  }

  return {
    targetQuery,
    publicationsQuery,
    publications,
    editionEntries,
    sourceEditionId,
    sourceInAnotherEdition,
    sourceEdition,
    sourceBpasQuery,
    sourceRows,
    target,
    candidates,
    targetName: target?.bpaName ?? "",
    sourceNameOf,
    urlContext,
  };
}

/** The preview for a selection. While the selection changes, the last preview of the same source stays on screen. */
export function useImportPreviewQuery(
  targetEditionBpaId: string | undefined,
  selection: ImportSelection | null,
) {
  return useQuery({
    queryKey: ["bpa-import-preview", targetEditionBpaId, selection],
    queryFn: () => researchCms.getImportPreview(targetEditionBpaId!, selection!),
    enabled: Boolean(targetEditionBpaId && selection),
    retry: false,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === targetEditionBpaId &&
      (previousQuery?.queryKey[2] as ImportSelection | null)?.sourceEditionBpaId ===
        selection?.sourceEditionBpaId
        ? previousData
        : undefined,
  });
}
